import { useCallback, useEffect, useMemo, useState } from "react";
import { GridSortModel } from "@mui/x-data-grid";
import { useToast } from "../../../Hooks/ToasContext";
import { IListSearchPayload } from "../../../Components/List/list.types";
import { ListOrdersReplacementUseCase } from "../../../Usecase/OrdersReplacement/list-orders-replacement-usecase";
import { IOrderReplacementEntity, IOrdersReplacementListPayload } from "../../../Services/api/orders-replacement/orders-replacement.interface";
import { DestroyOrdersReplacementUseCase } from "../../../Usecase/OrdersReplacement/destroy-orders-replacement-usecase";
import { GetMaskProductOptionsUseCase } from "../../../Usecase/Products/get-mask-product-options-usecase";
import { IOptionsProps } from "../../../Components/Form/Select/select.component";
import { format, parseISO } from "date-fns";
import { normalizeDateTimeZoneInString, normalizeDateToInputCalendar } from "../../../utils/chronos";
import { buildReplacementProductsSummary } from "../../../utils/build-replacement-products-summary";
import {
  buildReplacementSortModel,
  mapReplacementGridFieldToApi,
} from "../../../utils/replacement-order-list-sort";

interface IUseListOrdersReplacementProps {
  listOrdersReplacementUseCase: ListOrdersReplacementUseCase
  destroyOrdersReplacementUseCase: DestroyOrdersReplacementUseCase
  getMaskProductOptionsUseCase: GetMaskProductOptionsUseCase
}

export function useListOrdersReplacement({
  listOrdersReplacementUseCase,
  destroyOrdersReplacementUseCase,
  getMaskProductOptionsUseCase,
}: IUseListOrdersReplacementProps) {
    const [loading, setLoading] = useState(true)
    const [loadError, setLoadError] = useState(false)
    const [data, setData] = useState<IOrderReplacementEntity[]>([])
    const [orderToDestroy, setOrderToDestroy] = useState('')
    const [totalRegisters, setTotalRegisters] = useState(0)
    const [currentFilters, setCurrentFilters] = useState<IOrdersReplacementListPayload>({ page: 1 })
    const [currentSearch, setCurrentSearch] = useState('')
    const [brandOptions, setBrandOptions] = useState<IOptionsProps[]>([])

    const { showToast } = useToast()

    const listOrdersReplacement = useCallback(async (payload: Partial<IOrdersReplacementListPayload> & { page: number }) => {
        try {
          setLoading(true)
          setLoadError(false)

          const filters: IOrdersReplacementListPayload = {
            ...currentFilters,
            page: payload.page || currentFilters.page,
          }

          if ('searchBy' in payload) {
            filters.searchBy = payload.searchBy?.trim() || undefined
          }

          if ('brand' in payload) {
            filters.brand = payload.brand || undefined
          }

          if ('orderBy' in payload) {
            filters.orderBy = payload.orderBy
          }

          setCurrentFilters(filters)
          setCurrentSearch(filters.searchBy ?? '')

          const listResult = await listOrdersReplacementUseCase.handle(filters);

          if (listResult.isFailure) {
            setLoadError(true)
            showToast(String(listResult?.error?.error), 'error');
            return;
          }

         const list = listResult.getValue()

            setData(list?.data || [])
            setTotalRegisters(list?.total || 0)
            setLoadError(false)
        } catch (error) {
          setLoadError(true)
          console.error("error listOrdersReplacement function: ", error)
        } finally {
          setLoading(false);
        }
      }, [currentFilters, listOrdersReplacementUseCase, showToast]);

    const handleSearch = useCallback(({ page, searchBy }: IListSearchPayload) => {
      listOrdersReplacement({ page, searchBy: searchBy ?? '' })
    }, [listOrdersReplacement]);

    const handlePageChange = useCallback(({ page, searchBy }: IListSearchPayload) => {
      window.scrollTo({ top: 0, behavior: 'smooth' })
      listOrdersReplacement({
        page,
        searchBy: searchBy !== undefined ? searchBy : currentFilters.searchBy,
      })
    }, [currentFilters.searchBy, listOrdersReplacement]);

    const handleBrandFilter = useCallback((brand: string) => {
      listOrdersReplacement({
        page: 1,
        brand,
      })
    }, [listOrdersReplacement]);

    const handleSortModelChange = useCallback((sortModel: GridSortModel) => {
      if (sortModel.length > 0) {
        const { field, sort } = sortModel[0];
        if (!sort) {
          return;
        }

        listOrdersReplacement({
          page: 1,
          orderBy: {
            field: mapReplacementGridFieldToApi(field),
            order: sort.toUpperCase() as 'ASC' | 'DESC',
          },
        });
        return;
      }

      listOrdersReplacement({
        page: 1,
        orderBy: undefined,
      });
    }, [listOrdersReplacement]);

    const sortModel = useMemo(
      () => buildReplacementSortModel(currentFilters.orderBy),
      [currentFilters.orderBy],
    );

    const loadBrandOptions = useCallback(async () => {
      try {
        const result = await getMaskProductOptionsUseCase.handle();
        if (result.isFailure) {
          return;
        }

        const options = result.getValue();
        setBrandOptions(options?.brands ?? []);
      } catch (error) {
        console.log('error loadBrandOptions:', error);
      }
    }, [getMaskProductOptionsUseCase]);

    useEffect(() => {
      loadBrandOptions();
      listOrdersReplacement({ page: 1, searchBy: '' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const list = useMemo(() => {
        return data?.map((order) => {
          const date = normalizeDateTimeZoneInString(String(order.dueDate))
          const productsSummary = buildReplacementProductsSummary(order.products)

            return {
                ...order,
                productsSummaryLabel: productsSummary.label,
                productsSummaryDetail: productsSummary.detail,
                productsSummaryTooltip: productsSummary.tooltip,
                createdAt: format(parseISO(order.createdAt), 'dd/MM/yyyy'),
                dueDate: order?.dueDate ? normalizeDateToInputCalendar(date) : 'Não informado',
            }
        }) || []
    }, [data])

    const handleDestroyOrderReplacement = async () => {
      try {
        setLoading(true)

        const destroyResult = await destroyOrdersReplacementUseCase.handle(orderToDestroy);

        if (destroyResult.isFailure) {
          showToast(String(destroyResult?.error?.error), 'error');
          return;
        }

        setOrderToDestroy('')
        showToast('Removido com sucesso.', 'success');

       await listOrdersReplacement({
         page: currentFilters.page,
       })
      } catch (error) {
        console.log("error handleDestroyOrderReplacement function: ", error)
      } finally {
        setLoading(false);
      }
    };

    const orderToDestroyLabel = useMemo(() => {
      if (!orderToDestroy) return 'este pedido'
      return data.find((order) => order.id === orderToDestroy)?.identifier || orderToDestroy
    }, [orderToDestroy, data])

    return {
        loading,
        loadError,
        listOrdersReplacement,
        list,
        data,
        setOrderToDestroy,
        orderToDestroy,
        orderToDestroyLabel,
        handleDestroyOrderReplacement,
        totalRegisters,
        currentFilters,
        currentSearch,
        handleSearch,
        handlePageChange,
        handleBrandFilter,
        handleSortModelChange,
        sortModel,
        brandOptions,
    }
}
